import { useEffect, useState } from "react";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { money, moneyClass, cycleLabel } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DownloadSimple, Receipt, CalendarCheck, Stack, FilePdf, CheckCircle, XCircle } from "@phosphor-icons/react";

function currentCycle() { return new Date().toISOString().slice(0, 7); }

export default function Payouts() {
  const [cycle, setCycle] = useState(currentCycle());
  const [statements, setStatements] = useState([]);
  const [payTarget, setPayTarget] = useState(null);
  const [reps, setReps] = useState({});
  const [method, setMethod] = useState("ACH");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const openBulk = async () => {
    setPreview(null); setConfirmText(""); setRunResult(null); setBulkOpen(true);
    try { const r = await api.post("/payouts/bulk/preview", { cycle }); setPreview(r.data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setBulkOpen(false); }
  };

  const executeBulk = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/payouts/bulk/execute", { cycle, confirm: "PAY" });
      setRunResult(r.data);
      toast.success(`Paid ${r.data.count_paid} · skipped ${r.data.count_blocked}`);
      load(cycle);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const downloadPdf = (s) => window.open(`${API}/statements/${s.id}/pdf`, "_blank");

  const loadSchedule = () => api.get("/settings/payout-schedule").then((r) => setSchedule(r.data));
  useEffect(() => { loadSchedule(); }, []);

  const saveSchedule = async (next) => {
    setSchedule(next);
    try { await api.put("/settings/payout-schedule", next); toast.success("Schedule saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const runDraftNow = async () => {
    try { const r = await api.post("/settings/payout-schedule/run-now"); toast.success(`Drafted ${r.data.drafted || 0} statement(s) — no money moved`); load(cycle); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = (c) => {
    api.get(`/statements?cycle=${c}`).then((r) => setStatements(r.data));
    api.get("/reps").then((r) => { const m = {}; r.data.forEach((x) => { m[x.id] = x; }); setReps(m); });
  };
  useEffect(() => { load(cycle); }, [cycle]);

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post("/payouts/generate", { cycle }); setStatements(r.data.statements); toast.success(`Generated ${r.data.statements.length} statement(s)`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmPay = async () => {
    try {
      await api.post(`/statements/${payTarget.id}/pay`, { method, reference });
      toast.success(`Paid ${payTarget.rep_name}`);
      setPayTarget(null); setReference(""); load(cycle);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => window.open(`${API}/payouts/export?cycle=${cycle}`, "_blank");

  const stripePay = async (s) => {
    try { await api.post(`/statements/${s.id}/stripe-payout`); toast.success(`Stripe transfer sent to ${s.rep_name}`); load(cycle); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const totals = statements.reduce((a, s) => ({
    net: a.net + s.net_payable, gross: a.gross + s.gross_earned,
  }), { net: 0, gross: 0 });
  const unpaid = statements.filter((s) => s.status !== "paid" && s.net_payable !== 0);

  return (
    <div>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Payout Run</h1>
          <p className="mt-1 text-sm text-muted-foreground">Batch every statement for a cycle · review · mark paid · export CSV</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1.5">
            <Label className="overline">Cycle</Label>
            <Input data-testid="payout-cycle" type="month" value={cycle} onChange={(e) => setCycle(e.target.value)} className="font-mono" />
          </div>
          <Button data-testid="generate-payouts-btn" onClick={generate} disabled={busy}>{busy ? "Generating…" : "Generate Statements"}</Button>
          <Button data-testid="bulk-pay-btn" variant="outline" className="gap-2" onClick={openBulk}><Stack size={16} /> Pay all eligible</Button>
          <Button data-testid="export-csv-btn" variant="outline" className="gap-2" onClick={exportCsv}><DownloadSimple size={16} /> CSV</Button>
        </div>
      </div>

      {schedule && (
        <div data-testid="schedule-bar" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-sm border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <CalendarCheck size={20} className="text-primary" weight="fill" />
            <div>
              <div className="text-sm font-medium">Auto-draft monthly run <span className="font-normal text-muted-foreground">(draft only — a human always releases)</span></div>
              <div className="text-xs text-muted-foreground">Drafts every statement on day {schedule.day} · {schedule.timezone}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="overline">Day</Label>
            <Input data-testid="schedule-day" type="number" min={1} max={28} value={schedule.day}
              onChange={(e) => setSchedule({ ...schedule, day: Number(e.target.value) })}
              onBlur={() => saveSchedule(schedule)} className="w-16 font-mono" />
            <Switch data-testid="schedule-enabled" checked={schedule.enabled} onCheckedChange={(v) => saveSchedule({ ...schedule, enabled: v })} />
            <Button data-testid="run-draft-now-btn" variant="outline" size="sm" onClick={runDraftNow}>Run Draft Now</Button>
          </div>
        </div>
      )}

      <div className="mb-4 grid grid-cols-3 gap-4">
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Statements</div><div className="mt-1 font-mono text-2xl font-semibold">{statements.length}</div></div>
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Gross This Cycle</div><div className="mt-1 font-mono text-2xl font-semibold">{money(totals.gross)}</div></div>
        <div className="rounded-sm border border-border bg-card p-4"><div className="overline">Net Payable</div><div className="mt-1 font-mono text-2xl font-semibold text-primary">{money(totals.net)}</div></div>
      </div>

      <div className="overflow-hidden rounded-sm border border-border">
        <table className="w-full text-sm" data-testid="payouts-table">
          <thead className="bg-card"><tr className="border-b border-border">
            {["Rep", "Cycle", "Gross", "Adjustments", "Paid", "Net Payable", "Status", ""].map((c, i) => (
              <th key={i} className={`px-4 py-3 overline ${[2, 3, 4, 5].includes(i) ? "text-right" : "text-left"}`}>{c}</th>
            ))}
          </tr></thead>
          <tbody>
            {statements.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center">
                <Receipt size={32} className="mx-auto mb-3 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">No statements for {cycleLabel(cycle)}. Click Generate Statements.</div>
              </td></tr>
            )}
            {statements.map((s) => (
              <tr key={s.id} data-testid={`payout-row-${s.id}`} className="border-b border-border hover:bg-accent transition-colors duration-200">
                <td className="px-4 py-2.5 font-medium">{s.rep_name}</td>
                <td className="px-4 py-2.5 font-mono">{cycleLabel(s.cycle)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{money(s.gross_earned)}</td>
                <td className={`px-4 py-2.5 text-right font-mono ${moneyClass(s.adjustments)}`}>{money(s.adjustments)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-muted-foreground">{money(s.already_paid)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-medium">{money(s.net_payable)}</td>
                <td className="px-4 py-2.5"><StatusBadge status={s.status} /></td>
                <td className="px-4 py-2.5 text-right">
                  {s.status !== "paid" && s.net_payable !== 0 && (
                    <div className="flex justify-end gap-1.5">
                      {reps[s.rep_id]?.stripe_payouts_enabled ? (
                        <Button data-testid={`stripe-pay-${s.id}`} size="sm" onClick={() => stripePay(s)}>Pay via Stripe</Button>
                      ) : (
                        <span data-testid={`payout-blocked-${s.id}`} className="self-center font-mono text-[11px] text-[hsl(var(--warning))]">no Stripe</span>
                      )}
                      <Button data-testid={`mark-paid-${s.id}`} size="sm" variant="outline" onClick={() => { setPayTarget(s); setReference(""); }}>Mark Paid</Button>
                    </div>
                  )}
                  {s.status === "processing" && <span className="font-mono text-xs text-[hsl(var(--warning))]">processing…</span>}
                  {s.status === "paid" && (
                    <div className="flex items-center justify-end gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{s.method} · {s.reference}</span>
                      <button data-testid={`statement-pdf-${s.id}`} onClick={() => downloadPdf(s)} className="text-primary hover:text-primary/70 transition-colors" title="Download PDF statement"><FilePdf size={16} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Mark Paid — {payTarget?.rep_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-sm border border-border bg-card p-3">
              <div className="overline">Net Payable</div>
              <div className="font-mono text-2xl font-semibold text-primary">{money(payTarget?.net_payable)}</div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Wire">Wire</SelectItem>
                  <SelectItem value="Stripe">Stripe</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="PayPal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Reference</Label>
              <Input data-testid="pay-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TX-1001" className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-pay-btn" onClick={confirmPay}>Confirm Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setRunResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="bulk-preview-dialog">
          <DialogHeader><DialogTitle className="font-heading">Pay all eligible — {cycleLabel(cycle)}</DialogTitle></DialogHeader>

          {!runResult ? (
            <div className="space-y-4 py-2">
              {!preview ? (
                <div className="py-8 text-center font-mono text-sm text-muted-foreground">Computing eligibility…</div>
              ) : (
                <>
                  <div className="rounded-sm border border-border bg-card p-3">
                    <div className="overline">Total to pay · {preview.count} rep(s)</div>
                    <div data-testid="bulk-total" className="font-mono text-2xl font-semibold text-primary">{money(preview.total)}</div>
                  </div>

                  <div>
                    <div className="overline mb-1.5 flex items-center gap-1"><CheckCircle size={12} className="text-[hsl(var(--success))]" /> Eligible ({preview.eligible.length})</div>
                    <div data-testid="bulk-eligible-list" className="max-h-32 overflow-auto rounded-sm border border-border">
                      {preview.eligible.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Nobody is eligible for this cycle.</div>}
                      {preview.eligible.map((e, i) => (
                        <div key={i} className="flex justify-between border-b border-border px-3 py-1.5 text-sm last:border-0">
                          <span>{e.rep}</span><span className="font-mono">{money(e.net)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {preview.skipped.length > 0 && (
                    <div>
                      <div className="overline mb-1.5 flex items-center gap-1"><XCircle size={12} className="text-[hsl(var(--warning))]" /> Skipped ({preview.skipped.length})</div>
                      <div data-testid="bulk-skipped-list" className="max-h-28 overflow-auto rounded-sm border border-border">
                        {preview.skipped.map((s, i) => (
                          <div key={i} className="flex justify-between border-b border-border px-3 py-1.5 text-sm last:border-0">
                            <span>{s.rep}</span><span className="font-mono text-[11px] text-[hsl(var(--warning))]">{s.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5 rounded-sm border border-destructive/40 bg-destructive/5 p-3">
                    <Label className="overline text-destructive">This moves real money · type <span className="font-mono">PAY</span> to confirm</Label>
                    <Input data-testid="bulk-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="PAY" className="font-mono" />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-2" data-testid="bulk-run-result">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-sm border border-border bg-card p-3"><div className="overline">Paid</div><div data-testid="run-count-paid" className="font-mono text-2xl font-semibold text-[hsl(var(--success))]">{runResult.count_paid}</div></div>
                <div className="rounded-sm border border-border bg-card p-3"><div className="overline">Skipped</div><div data-testid="run-count-blocked" className="font-mono text-2xl font-semibold text-[hsl(var(--warning))]">{runResult.count_blocked}</div></div>
              </div>
              {runResult.lines?.length > 0 && (
                <div className="max-h-32 overflow-auto rounded-sm border border-border">
                  {runResult.lines.map((l, i) => (
                    <div key={i} className="flex justify-between border-b border-border px-3 py-1.5 text-sm last:border-0">
                      <span>{l.rep}</span><span className="font-mono text-xs text-muted-foreground">{l.method} · {l.reference}</span>
                    </div>
                  ))}
                </div>
              )}
              {runResult.blocked?.length > 0 && (
                <div className="max-h-28 overflow-auto rounded-sm border border-border">
                  {runResult.blocked.map((b, i) => (
                    <div key={i} className="flex justify-between border-b border-border px-3 py-1.5 text-sm last:border-0">
                      <span>{b.rep}</span><span className="font-mono text-[11px] text-[hsl(var(--warning))]">{b.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!runResult ? (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button data-testid="bulk-execute-btn" disabled={confirmText !== "PAY" || bulkBusy || !preview || preview.count === 0} onClick={executeBulk}>
                  {bulkBusy ? "Paying…" : `Pay ${preview?.count || 0} rep(s)`}
                </Button>
              </>
            ) : (
              <Button data-testid="bulk-close-btn" onClick={() => { setBulkOpen(false); setRunResult(null); }}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
